const LAYOUT_VERSION = {
  new: 'new',
  legacy: 'legacy',
} as const

/** Registrations on this calendar day and later default to the new layout. */
const NEW_LAYOUT_DEFAULT_FROM = '2026-10-14'

export type LayoutVersion = typeof LAYOUT_VERSION[keyof typeof LAYOUT_VERSION]

/** True when `dateCreated` falls on or after the new-layout release day. */
export function isRegisteredFromNewLayoutDefault(dateCreated: string | null | undefined): boolean {
  if (!dateCreated)
    return false

  const day = dateCreated.slice(0, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && day >= NEW_LAYOUT_DEFAULT_FROM
}

// Accounts created on or after 14 October 2026 stay on the new layout.
// For everyone else, an explicit cookie choice wins and no choice means legacy.
export function resolveIsNewLayout(
  isToggleEnabled: boolean,
  storedVersion: LayoutVersion | null | undefined,
  dateCreated: string | null | undefined,
): boolean {
  if (!isToggleEnabled)
    return false
  if (isRegisteredFromNewLayoutDefault(dateCreated))
    return true
  return storedVersion === LAYOUT_VERSION.new
}

/** Older accounts can still switch. New registrations cannot. */
export function canSwitchLayoutVersion(
  isToggleEnabled: boolean,
  dateCreated: string | null | undefined,
  hasUserData: boolean,
): boolean {
  if (!isToggleEnabled || !hasUserData)
    return false
  return !isRegisteredFromNewLayoutDefault(dateCreated)
}

/** Integer sent to GA as `nav_version`: 1 = legacy chrome, 2 = new chrome (header + navbar). */
export function resolveLayoutVersionNumber(isNewLayoutVersion: boolean): 1 | 2 {
  return isNewLayoutVersion ? 2 : 1
}

// append nav version to source when adding items to the cart
export function appendNavVersionToSource(source: string | undefined, navVersion: 1 | 2): string {
  const base = source
    || (import.meta.client ? location.href : '')
  if (!base)
    return String(navVersion)

  try {
    const url = new URL(base)
    url.searchParams.set('nav_version', String(navVersion))
    return url.toString()
  }
  catch {
    const separator = base.includes('?') ? '&' : '?'
    return `${base}${separator}nav_version=${navVersion}`
  }
}

export function useLayoutVersion() {
  const { feature } = useCountryConfig()
  const userStore = useUserStore()
  // null means the user has never chosen a layout. The previous default of
  // "legacy" made that indistinguishable from an explicit legacy choice.
  const version = useCookie<LayoutVersion | null>('layout-version', {
    default: () => null,
    maxAge: 60 * 60 * 24 * 365,
  })

  const isToggleEnabled = computed(() => Boolean(feature.displayLayoutVersionToggle))

  const isNew = computed({
    get: () => resolveIsNewLayout(
      isToggleEnabled.value,
      version.value,
      userStore.data?.dateCreated,
    ),
    set: (value: boolean) => {
      version.value = value ? LAYOUT_VERSION.new : LAYOUT_VERSION.legacy
    },
  })

  const canSwitchLayout = computed(() => canSwitchLayoutVersion(
    isToggleEnabled.value,
    userStore.data?.dateCreated,
    !!userStore.data,
  ))

  return { version, isNew, canSwitchLayout }
}
